import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { SortableHeader, useSort, ResetViewButton } from '../ui/SortableHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Plus, Edit, Trash2, Globe, Mail, Phone, Upload } from 'lucide-react';
import BulkImportDialog from './BulkImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: '', contact_email: '', contact_phone: '', website: '', address: '', notes: '' };

const SuppliersList = ({ suppliers, isAdmin, onRefresh }) => {
  const { t } = useTranslation();
  const [formOpen, setFormOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('name', 'asc', 'inventory-suppliers');
  const isViewCustomized = sortKey !== 'name' || sortDir !== 'asc';

  const openCreate = () => { setForm({ ...EMPTY }); setEditSupplier(null); setFormOpen(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, contact_email: s.contact_email || '', contact_phone: s.contact_phone || '', website: s.website || '', address: s.address || '', notes: s.notes || '' });
    setEditSupplier(s);
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('inv.suppliers.nameRequired')); return; }
    setSaving(true);
    try {
      if (editSupplier) {
        await axios.put(`${API}/inventory/suppliers/${editSupplier.id}`, form);
        toast.success(t('inv.suppliers.updated'));
      } else {
        await axios.post(`${API}/inventory/suppliers`, form);
        toast.success(t('inv.suppliers.created'));
      }
      setFormOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/inventory/suppliers/${deleteId}`);
      toast.success(t('inv.suppliers.deleted'));
      setDeleteId(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  return (
    <div className="space-y-4" data-testid="suppliers-list">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">{t('inv.suppliers.title', { count: suppliers.length })}</CardTitle>
          <div className="flex items-center gap-3">
            <ResetViewButton show={isViewCustomized} onReset={resetSort} testId="reset-view-suppliers" />
            {isAdmin && (
              <>
                <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
                  data-testid="import-suppliers-btn">
                  <Upload className="w-4 h-4 mr-1" /> {t('inv.import.button')}
                </Button>
                <Button size="sm" onClick={openCreate} data-testid="create-supplier-btn">
                  <Plus className="w-4 h-4 mr-1" /> {t('inv.suppliers.new')}
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {suppliers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-suppliers-message">{t('inv.suppliers.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('common.name')}</SortableHeader>
                  <SortableHeader column="contact_email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('common.email')}</SortableHeader>
                  <SortableHeader column="contact_phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('common.phone')}</SortableHeader>
                  <SortableHeader column="website" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('inv.suppliers.website')}</SortableHeader>
                  {isAdmin && <TableHead className="w-[80px]">{t('common.actions')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...suppliers].sort(comparator((s, key) => {
                  switch (key) {
                    case 'name': return (s.name || '').toLowerCase();
                    case 'contact_email': return (s.contact_email || '').toLowerCase();
                    case 'contact_phone': return s.contact_phone || '';
                    case 'website': return (s.website || '').toLowerCase();
                    default: return '';
                  }
                })).map((s) => (
                  <TableRow key={s.id}
                    className={isAdmin ? 'cursor-pointer hover:bg-muted/50' : undefined}
                    onClick={isAdmin ? () => openEdit(s) : undefined}
                    data-testid={`supplier-row-${s.name}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.contact_email ? <span className="flex items-center gap-1 text-sm"><Mail className="w-3 h-3" />{s.contact_email}</span> : '-'}</TableCell>
                    <TableCell>{s.contact_phone ? <span className="flex items-center gap-1 text-sm"><Phone className="w-3 h-3" />{s.contact_phone}</span> : '-'}</TableCell>
                    <TableCell>{s.website ? <a href={s.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-sm text-blue-600"><Globe className="w-3 h-3" />{t('inv.suppliers.link')}</a> : '-'}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.name}`}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setDeleteId(s.id)} data-testid={`delete-supplier-${s.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="supplier-form-dialog">
          <DialogHeader><DialogTitle>{editSupplier ? t('inv.suppliers.edit') : t('inv.suppliers.new')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label className="text-xs">{t('common.name')} *</Label><Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))} required data-testid="supplier-name-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t('common.email')}</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm(prev => ({ ...prev, contact_email: e.target.value  }))} data-testid="supplier-email-input" /></div>
              <div><Label className="text-xs">{t('common.phone')}</Label><Input value={form.contact_phone} onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value  }))} data-testid="supplier-phone-input" /></div>
            </div>
            <div><Label className="text-xs">{t('inv.suppliers.website')}</Label><Input value={form.website} onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value  }))} placeholder={t('auto.inv.https')} data-testid="supplier-website-input" /></div>
            <div><Label className="text-xs">{t('inv.common.address')}</Label><Input value={form.address} onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value  }))} data-testid="supplier-address-input" /></div>
            <div><Label className="text-xs">{t('common.notes')}</Label><Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value  }))} rows={2} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving} data-testid="save-supplier-btn">{saving ? t('inv.common.saving') : editSupplier ? t('inv.common.update') : t('inv.common.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inv.suppliers.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('inv.suppliers.deleteHint')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-supplier">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} kind="suppliers"
        onImported={() => onRefresh?.()} />
    </div>
  );
};

export default SuppliersList;
